import { useEffect, useState } from 'react';
import qrcode from 'qrcode-generator';
import {
  STEP_MESSAGE,
  confirmEnrollment,
  finishEnrollment,
  groupKey,
  managedLogin,
  recoveryCodesText,
  startEnrollment,
  submitRecoveryCode,
  verifyCode,
  type Enrollment,
  type PendingStep,
  type StepError,
} from '../services/managedSignIn';

// RadioOS-managed sign-in: e-mail + password, then (when the account requires it) an
// authenticator app. Authentication only - the shell the user then sees is decided by
// authorization (GET /api/auth/me), never chosen here.

const field = 'w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm';
const primary = 'w-full px-4 py-2.5 rounded bg-blue-600 hover:bg-blue-500 font-semibold disabled:opacity-40';

/** The otpauth URI as a QR code, drawn locally as SVG rectangles (no remote service, no innerHTML). */
export function QRCode({ value, label }: { value: string; label: string }) {
  const qr = qrcode(0, 'M');
  qr.addData(value);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 4;
  let path = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) path += `M${c + quiet},${r + quiet}h1v1h-1z`;
    }
  }
  const size = n + 2 * quiet;
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${size} ${size}`} width="192" height="192" shapeRendering="crispEdges" className="mx-auto bg-white rounded">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

export function PasswordForm({ busy, error, onSubmit }: { busy?: boolean; error?: string; onSubmit: (email: string, password: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  return (
    <form className="space-y-3 text-left" onSubmit={(e) => { e.preventDefault(); onSubmit(email.trim(), password); }}>
      <div>
        <label htmlFor="signin-email" className="block text-xs text-gray-300 mb-1">E-mail</label>
        <input id="signin-email" name="username" type="email" autoComplete="username" className={field} value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <label htmlFor="signin-password" className="block text-xs text-gray-300 mb-1">Password</label>
        <input id="signin-password" name="password" type="password" autoComplete="current-password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      {error && <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{error}</p>}
      <button type="submit" className={primary} disabled={busy || !email.trim() || !password}>{busy ? 'Signing in…' : 'Sign in'}</button>
    </form>
  );
}

export function CodeForm({ mode, busy, error, onSubmit, onSwitch }: {
  mode: 'otp' | 'recovery'; busy?: boolean; error?: string; onSubmit: (code: string) => void; onSwitch: () => void;
}) {
  const [code, setCode] = useState('');
  const otp = mode === 'otp';
  return (
    <form className="space-y-3 text-left" onSubmit={(e) => { e.preventDefault(); onSubmit(code.trim()); setCode(''); }}>
      <p className="text-sm text-gray-300">
        {otp ? 'Enter the 6-digit code from your authenticator app.' : 'Enter one of your recovery codes. Each code works only once.'}
      </p>
      <div>
        <label htmlFor="signin-code" className="block text-xs text-gray-300 mb-1">{otp ? 'Authentication code' : 'Recovery code'}</label>
        <input id="signin-code" name={otp ? 'one-time-code' : 'recovery-code'} autoComplete={otp ? 'one-time-code' : 'off'}
          inputMode={otp ? 'numeric' : 'text'} maxLength={otp ? 7 : 11} className={`${field} font-mono tracking-widest`} value={code} onChange={(e) => setCode(e.target.value)} />
      </div>
      {error && <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{error}</p>}
      <button type="submit" className={primary} disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Continue'}</button>
      <button type="button" className="text-xs text-blue-300 underline" onClick={onSwitch}>{otp ? 'Use a recovery code' : 'Use the authenticator app'}</button>
    </form>
  );
}

export function EnrollView({ enrollment, busy, error, onSubmit }: { enrollment: Enrollment; busy?: boolean; error?: string; onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('');
  return (
    <form className="space-y-4 text-left" onSubmit={(e) => { e.preventDefault(); onSubmit(code.trim()); setCode(''); }}>
      <h2 className="text-lg font-semibold">Set up your authenticator app</h2>
      <ol className="text-sm text-gray-300 list-decimal pl-5 space-y-1">
        <li>Open an authenticator app on your phone and add a new account.</li>
        <li>Scan the QR code, or enter the setup key manually.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      <QRCode value={enrollment.otpauth_uri} label={`QR code for ${enrollment.issuer} (${enrollment.account})`} />
      <div>
        <p className="text-xs text-gray-400">Setup key ({enrollment.account}, time-based, {enrollment.digits} digits)</p>
        <p className="font-mono text-sm break-all select-all bg-gray-900 border border-gray-700 rounded p-2" aria-label="Setup key">{groupKey(enrollment.secret)}</p>
      </div>
      <div>
        <label htmlFor="enroll-code" className="block text-xs text-gray-300 mb-1">Code from the app</label>
        <input id="enroll-code" name="one-time-code" autoComplete="one-time-code" inputMode="numeric" maxLength={7}
          className={`${field} font-mono tracking-widest`} value={code} onChange={(e) => setCode(e.target.value)} />
      </div>
      {error && <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{error}</p>}
      <button type="submit" className={primary} disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Verify'}</button>
    </form>
  );
}

export function RecoveryCodesView({ codes, account, busy, error, onContinue }: {
  codes: string[]; account: string; busy?: boolean; error?: string; onContinue: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = recoveryCodesText(codes, account);
  const download = () => {
    // Built in memory and handed to the browser's download; never sent anywhere or stored.
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'radioos-recovery-codes.txt' });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4 text-left">
      <h2 className="text-lg font-semibold">Save your recovery codes</h2>
      <p className="text-sm text-yellow-200">These codes are shown only once and cannot be shown again.</p>
      <p className="text-sm text-gray-300">If your authenticator app is not available, each code lets you sign in once. Store them somewhere safe.</p>
      <ol aria-label="Recovery codes" className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-900 border border-gray-700 rounded p-3">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ol>
      <div className="flex gap-2">
        <button type="button" className="px-3 py-1.5 rounded text-sm bg-gray-700 hover:bg-gray-600"
          onClick={() => navigator.clipboard?.writeText(text).then(() => setCopied(true), () => setCopied(false))}>{copied ? 'Copied' : 'Copy codes'}</button>
        <button type="button" className="px-3 py-1.5 rounded text-sm bg-gray-700 hover:bg-gray-600" onClick={download}>Download as text</button>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>I have saved my recovery codes.</span>
      </label>
      {error && <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{error}</p>}
      <button type="button" className={primary} disabled={!saved || busy} onClick={onContinue}>{busy ? 'Signing in…' : 'Continue'}</button>
    </div>
  );
}

type Stage =
  | { kind: 'password' }
  | { kind: 'again' }
  | { kind: 'verify'; challenge: string; mode: 'otp' | 'recovery' }
  | { kind: 'enroll-loading'; challenge: string }
  | { kind: 'enroll'; challenge: string; enrollment: Enrollment }
  | { kind: 'codes'; challenge: string; codes: string[]; account: string };

/**
 * The whole managed sign-in; on success the page reloads into the authorized application. With
 * `pending`, it continues a Microsoft/Google sign-in at its RadioOS second factor (step-up): no
 * password stage, and an expired step means signing in with that provider again.
 */
export default function ManagedSignIn({ onSignedIn = () => window.location.reload(), pending }: { onSignedIn?: () => void; pending?: PendingStep }) {
  // Everything below lives in component memory only.
  const [stage, setStage] = useState<Stage>(() => !pending ? { kind: 'password' }
    : pending.next === 'verify' ? { kind: 'verify', challenge: pending.challenge, mode: 'otp' } : { kind: 'enroll-loading', challenge: pending.challenge });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const restart = (message?: string) => { setStage(pending ? { kind: 'again' } : { kind: 'password' }); setError(message); };
  const failed = (e: StepError) => {
    if (e === 'sign_in_again') restart(STEP_MESSAGE.sign_in_again);
    else setError(STEP_MESSAGE[e]);
  };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try { await fn(); } finally { setBusy(false); }
  };

  // A pending enrolment (step-up): fetch the authenticator setup once.
  useEffect(() => {
    if (!pending || pending.next !== 'enroll') return;
    let live = true;
    startEnrollment(pending.challenge).then((e) => {
      if (!live) return;
      if ('error' in e) failed(e.error);
      else setStage({ kind: 'enroll', challenge: pending.challenge, enrollment: e.ok });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = (email: string, password: string) => run(async () => {
    const r = await managedLogin(email, password);
    if (r.kind === 'authenticated') return onSignedIn();
    if (r.kind === 'invalid') return setError(r.message);
    if (r.kind === 'rate_limited') return setError(STEP_MESSAGE.rate_limited);
    if (r.kind === 'unavailable') return setError(STEP_MESSAGE.unavailable);
    if (r.next === 'verify') return setStage({ kind: 'verify', challenge: r.challenge, mode: 'otp' });
    setStage({ kind: 'enroll-loading', challenge: r.challenge });
    const e = await startEnrollment(r.challenge);
    if ('error' in e) return failed(e.error);
    setStage({ kind: 'enroll', challenge: r.challenge, enrollment: e.ok });
  });

  if (stage.kind === 'password') return <PasswordForm busy={busy} error={error} onSubmit={signIn} />;
  if (stage.kind === 'again') {
    return (
      <div className="space-y-3">
        {error && <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{error}</p>}
        <a href="/" className={primary + ' block text-center'}>Sign in again</a>
      </div>
    );
  }
  if (stage.kind === 'enroll-loading') return <p role="status" className="text-sm text-gray-300">Preparing authenticator setup…</p>;
  if (stage.kind === 'verify') {
    return (
      <CodeForm mode={stage.mode} busy={busy} error={error}
        onSwitch={() => { setError(undefined); setStage({ ...stage, mode: stage.mode === 'otp' ? 'recovery' : 'otp' }); }}
        onSubmit={(code) => run(async () => {
          const r = stage.mode === 'otp' ? await verifyCode(stage.challenge, code) : await submitRecoveryCode(stage.challenge, code);
          if ('error' in r) return failed(r.error);
          onSignedIn();
        })} />
    );
  }
  if (stage.kind === 'enroll') {
    return (
      <EnrollView enrollment={stage.enrollment} busy={busy} error={error}
        onSubmit={(code) => run(async () => {
          const r = await confirmEnrollment(stage.challenge, code);
          if ('error' in r) return failed(r.error);
          // The setup secret is dropped from memory as soon as the codes are shown.
          setStage({ kind: 'codes', challenge: stage.challenge, codes: r.ok, account: stage.enrollment.account });
        })} />
    );
  }
  return (
    <RecoveryCodesView codes={stage.codes} account={stage.account} busy={busy} error={error}
      onContinue={() => run(async () => {
        const r = await finishEnrollment(stage.challenge);
        if ('error' in r) return failed(r.error);
        onSignedIn();
      })} />
  );
}
